import { useState, useRef, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import { ArrowRight, ChevronLeft, ChevronRight, X, BookOpen, Bell } from 'lucide-react';
import SEO from '@/components/SEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C8A840';

const BOOK_COVERS = {
  front: 'https://customer-assets.emergentagent.com/job_22d79c15-8eba-437a-975f-6355374d527d/artifacts/rgvc8ddx_SBT_COVER_FRONT.png',
  back: 'https://customer-assets.emergentagent.com/job_22d79c15-8eba-437a-975f-6355374d527d/artifacts/osh78zbj_SBT_COVER_BACK.png',
};

const PageCover = forwardRef(({ children, className = '' }, ref) => (
  <div ref={ref} className={`page-cover ${className}`}>{children}</div>
));
PageCover.displayName = 'PageCover';

const PageContent = forwardRef(({ children, number, dark = false }, ref) => (
  <div ref={ref} className="relative" style={{ backgroundColor: dark ? '#08080C' : '#FFFDF8', boxShadow: dark ? 'none' : 'inset 0 0 30px rgba(0,0,0,0.05)' }}>
    <div className="w-full h-full p-5 md:p-6 flex flex-col overflow-hidden" style={{ backgroundColor: dark ? '#08080C' : '#FFFDF8' }}>
      {children}
    </div>
    {number && (
      <div className="absolute bottom-2 left-0 right-0 text-center">
        <span className="text-[9px] tracking-widest" style={{ fontFamily: "'DM Mono', monospace", color: dark ? 'rgba(200,164,72,0.4)' : 'rgba(13,13,13,0.3)' }}>{number}</span>
      </div>
    )}
  </div>
));
PageContent.displayName = 'PageContent';

const BookLaunchPage = () => {
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyDone, setNotifyDone] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const bookRef = useRef(null);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!notifyEmail) return;
    setNotifyLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email: notifyEmail });
    } catch {}
    setNotifyDone(true);
    setNotifyLoading(false);
  };

  const nextPage = () => { if (bookRef.current) bookRef.current.pageFlip().flipNext(); };
  const prevPage = () => { if (bookRef.current) bookRef.current.pageFlip().flipPrev(); };
  const onFlip = (e) => setCurrentPage(e.data);
  const totalPages = 18;

  return (
    <div data-testid="book-launch-page" className="min-h-screen bg-[#08080C]">
      <SEO
        title="She Built This: The Book — by Dr. Neha Dewan"
        description="Edition 1. Fifty years of research. Zero programs proven to work. 595 women who built anyway."
        image={BOOK_COVERS.front}
        type="book"
        author="Dr. Neha Dewan"
      />

      {/* ═══════════════════════════════════════════
          HERO — Full cinematic book reveal
          ═══════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Film grain */}
        <div className="absolute inset-0 film-grain-overlay opacity-30 pointer-events-none z-10" />
        {/* Vignette */}
        <div className="absolute inset-0 z-10 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)' }} />

        <div className="relative z-20 max-w-6xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

            {/* Left — Typography */}
            <div className="order-2 lg:order-1">
              <p className="mb-8" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.35em', color: GOLD }}>
                EDITION 1 &middot; 21 CHAPTERS &middot; 595 WOMEN
              </p>

              <h1 className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(48px, 7vw, 88px)', fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1, color: '#fff' }}>
                SHE BUILT<br />THIS
              </h1>

              <div className="w-16 h-[1px] mb-8" style={{ background: `linear-gradient(to right, ${GOLD}, transparent)` }} />

              <p className="text-xl lg:text-2xl italic leading-relaxed mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: 'rgba(255,255,255,0.45)' }}>
                Fifty Years. Zero Proof.
              </p>
              <p className="text-lg lg:text-xl italic leading-relaxed mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: 'rgba(255,255,255,0.3)' }}>
                Five Hundred and Ninety-Five Women<br />Who Built Anyway.
              </p>

              <p className="mb-10" style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)' }}>
                Dr. Neha Dewan
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsReaderOpen(true)}
                  data-testid="read-first-chapters-btn"
                  className="group px-8 py-4 border transition-all duration-500 hover:bg-[rgba(200,168,64,0.08)] flex items-center justify-center gap-3"
                  style={{ borderColor: GOLD, fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: GOLD }}
                >
                  <BookOpen size={14} />
                  READ FIRST CHAPTERS
                </button>
                <a
                  href="https://a.co/d/0jdiF8K1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buy-on-amazon-btn"
                  className="px-8 py-4 border border-[#1A1A1A]/10 transition-all duration-500 hover:border-white/30 flex items-center justify-center gap-3"
                  style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.4)' }}
                >
                  BUY ON KINDLE <ArrowRight size={14} />
                </a>
              </div>
            </div>

            {/* Right — Book cover */}
            <div className="order-1 lg:order-2 flex justify-center relative">
              <div className="absolute inset-0 rounded-full blur-3xl opacity-15" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 70%)`, transform: 'scale(0.7)' }} />
              <div className="cursor-pointer group" onClick={() => setIsReaderOpen(true)}>
                <img
                  src={BOOK_COVERS.front}
                  alt="She Built This — Book Cover"
                  data-testid="book-cover-hero"
                  className="w-[260px] md:w-[320px] lg:w-[360px] h-auto relative z-10 transition-transform duration-700 group-hover:scale-[1.02]"
                  style={{ filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.6))', animation: 'bookFloat 5s ease-in-out infinite alternate' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          THE FINDING — Dramatic stat reveal
          ═══════════════════════════════════════════ */}
      <section className="relative py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 film-grain-overlay opacity-15 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <p className="mb-8" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.4em', color: 'rgba(255,255,255,0.25)' }}>
            THE FINDING
          </p>

          <p className="text-lg lg:text-xl leading-relaxed mb-12" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: 'rgba(255,255,255,0.4)' }}>
            The global investment in women's leadership development<br />
            now exceeds eight billion dollars annually.
          </p>

          <p className="mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.35em', color: 'rgba(255,255,255,0.2)' }}>
            PROGRAMMES PROVEN TO PRODUCE THE OUTCOME THEY PROMISED
          </p>
          <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(100px, 16vw, 180px)', fontWeight: 700, lineHeight: 1, color: '#fff', textShadow: '0 0 120px rgba(255,255,255,0.1)' }}>
            0
          </p>
          <p className="mt-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.35em', color: GOLD }}>
            FIFTY YEARS. ZERO.
          </p>

          <div className="w-24 h-[1px] mx-auto mt-16 mb-16" style={{ background: `linear-gradient(to right, transparent, ${GOLD}, transparent)` }} />

          <p className="text-lg italic max-w-2xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: 'rgba(255,255,255,0.35)', lineHeight: 1.8 }}>
            "Zero does not mean women cannot lead. Five hundred and ninety-five verified
            profiles say otherwise. Zero means we have been solving the wrong problem
            for fifty years."
          </p>
          <p className="mt-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.15)' }}>
            — FROM CHAPTER ONE
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          THE EXCERPT — From the Prologue
          ═══════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-32" style={{ background: 'linear-gradient(180deg, rgba(200,168,64,0.03) 0%, transparent 100%)' }}>
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <p className="text-center mb-16" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.4em', color: GOLD }}>
            FROM THE PROLOGUE
          </p>

          <div className="space-y-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(18px, 2.2vw, 22px)', fontWeight: 300, color: 'rgba(255,255,255,0.5)', lineHeight: 2 }}>
            <p>She had prepared for three weeks. Not because the meeting required three weeks of preparation. It did not.</p>
            <p>But she had learned, over the years, that preparation was her armour. Not confidence. Not seniority. Not the accumulated weight of a track record that was, by any objective measure, stronger than almost anyone else in that room.</p>
            <p style={{ color: '#fff' }}>She spoke first.</p>
            <p>When she finished, the room was quiet. Not the quiet of consideration. The particular quiet of a room that has decided, collectively and without discussion, to respond to what was just said by saying nothing.</p>
            <p style={{ color: GOLD, fontStyle: 'italic' }}>Four minutes passed. She counted them.</p>
            <p>Four minutes later, a man on the opposite side of the table said almost exactly what she had said. He said it less precisely. He said it with less data. But he said it with a particular quality of voice that certain rooms are designed to hear.</p>
            <p style={{ color: '#fff', fontWeight: 500 }}>Her name was not in the minutes.</p>
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => setIsReaderOpen(true)}
              className="px-10 py-4 border transition-all duration-500 hover:bg-[rgba(200,168,64,0.08)]"
              style={{ borderColor: GOLD, fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: GOLD }}
            >
              CONTINUE READING
            </button>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          THE AUTHOR
          ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 relative">
        <div className="absolute inset-0 film-grain-overlay opacity-10 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-16 items-start">
            <div>
              <p className="mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.35em', color: GOLD }}>
                THE AUTHOR
              </p>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                Dr. Neha<br />Dewan
              </h3>
              <div className="w-12 h-[1px] mt-6" style={{ background: GOLD }} />
            </div>
            <div className="space-y-5" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 300, color: 'rgba(255,255,255,0.4)', lineHeight: 1.85 }}>
              <p>
                A governance researcher who spent years documenting what no programme had ever
                been designed to measure: the women who built institutions, companies, schools,
                and movements without waiting for the system to invite them.
              </p>
              <p>
                She Built This began as a research project. It became a platform documenting
                595 women across 14 regions and every continent. Then it became a book. Then a film.
              </p>
              <p style={{ color: 'rgba(255,255,255,0.6)' }}>
                The question at the centre of all of it remains the same:
              </p>
              <p className="italic text-xl" style={{ color: GOLD }}>
                "What does it cost a person to build something that will never carry their name?"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          WHAT THE BOOK CONTAINS
          ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-28 relative" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center mb-16" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.4em', color: GOLD }}>
            WHAT THIS BOOK CONTAINS
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 text-center">
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '48px', fontWeight: 700, color: '#fff' }}>21</p>
              <p className="mt-2 mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.25)' }}>CHAPTERS</p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontWeight: 300, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7 }}>
                Each one a different lens on what it means to build when the system was not designed for you.
              </p>
            </div>
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '48px', fontWeight: 700, color: GOLD }}>595</p>
              <p className="mt-2 mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.25)' }}>WOMEN DOCUMENTED</p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontWeight: 300, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7 }}>
                Verified profiles across 14 regions, every continent. Founders, heads of state, scientists, builders.
              </p>
            </div>
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '48px', fontWeight: 700, color: '#fff' }}>50</p>
              <p className="mt-2 mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.25)' }}>YEARS OF RESEARCH</p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontWeight: 300, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7 }}>
                The evidence that no one wanted to publish. The finding that changes the conversation entirely.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          BUY / READ CTAs — Premium
          ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-28 relative" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="absolute inset-0 film-grain-overlay opacity-10 pointer-events-none" />
        <div className="max-w-2xl mx-auto px-6 text-center relative z-10">
          <p className="mb-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.4em', color: GOLD }}>
            AVAILABLE NOW
          </p>
          <h3 className="mb-4" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
            Read the book that<br />started the movement.
          </h3>
          <p className="mb-12" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 300, color: 'rgba(255,255,255,0.3)' }}>
            Edition 1 &middot; 21 chapters &middot; Amazon Kindle
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => setIsReaderOpen(true)}
              className="px-10 py-4 border transition-all duration-500 hover:bg-[rgba(200,168,64,0.08)]"
              style={{ borderColor: GOLD, fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: GOLD }}
            >
              <BookOpen size={14} className="inline mr-3 mb-[2px]" />
              READ FREE CHAPTERS
            </button>
            <a
              href="https://a.co/d/0jdiF8K1"
              target="_blank"
              rel="noopener noreferrer"
              className="px-10 py-4 transition-all duration-500 hover:opacity-80"
              style={{ background: GOLD, fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: '#08080C' }}
            >
              BUY ON KINDLE <ArrowRight size={14} className="inline ml-2 mb-[2px]" />
            </a>
          </div>

          <p className="mt-8" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.12)' }}>
            &copy; 2025 SHE BUILT THIS &middot; UAE COPYRIGHT REGISTERED &middot; AMAZON KDP
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          EDITION 2 TEASER
          ═══════════════════════════════════════════ */}
      <section className="relative py-24 lg:py-28" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.35em', color: GOLD }}>
            APRIL 27TH, 2026
          </p>
          <div className="w-10 h-[1px] mx-auto mb-8" style={{ background: `linear-gradient(to right, transparent, ${GOLD}, transparent)` }} />
          <h3 className="mb-6" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 700, color: '#fff', lineHeight: 1.05 }} data-testid="edition-2-title">
            Edition 2 is coming.
          </h3>
          <p className="text-lg italic mb-12" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: 'rgba(255,255,255,0.3)' }}>
            197 Women. One Truth. Built Anyway.<br />Read three preview chapters now.
          </p>

          <div className="grid grid-cols-3 gap-8 max-w-md mx-auto mb-12">
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, color: GOLD }}>197</p>
              <p className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.2)' }}>WOMEN</p>
            </div>
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, color: '#fff' }}>3</p>
              <p className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.2)' }}>PREVIEW CHAPTERS</p>
            </div>
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, color: GOLD }}>New</p>
              <p className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.2)' }}>RESEARCH</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/book/edition-2"
              data-testid="read-edition2-preview-btn"
              className="inline-flex items-center gap-2 px-8 py-4 transition-all duration-500 hover:opacity-90"
              style={{ background: GOLD, fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: '#08080C' }}
            >
              <BookOpen size={14} />
              READ EDITION 2 PREVIEW
            </Link>
            <Link
              to="/nominate"
              data-testid="edition-2-nominate-btn"
              className="inline-flex items-center gap-2 px-8 py-4 border transition-all duration-500 hover:bg-[rgba(200,168,64,0.08)]"
              style={{ borderColor: 'rgba(200,168,64,0.3)', fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: GOLD }}
            >
              NOMINATE A WOMAN <ArrowRight size={14} />
            </Link>
          </div>

          {/* Email capture */}
          <div className="mt-16 max-w-md mx-auto">
            <p className="mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.2)' }}>
              <Bell size={10} className="inline mr-2 mb-[1px]" />
              GET NOTIFIED WHEN EDITION 2 DROPS
            </p>
            {notifyDone ? (
              <div data-testid="edition2-subscribed" className="py-3 px-6 border" style={{ borderColor: GOLD }}>
                <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.15em', color: GOLD }}>
                  YOU'RE ON THE LIST.
                </p>
              </div>
            ) : (
              <form onSubmit={handleNotify} className="flex gap-2" data-testid="edition2-notify-form">
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="edition2-email-input"
                  className="flex-1 h-12 px-5 bg-transparent border border-[#1A1A1A]/10 text-white placeholder:text-white/15 outline-none focus:border-[rgba(200,168,64,0.4)] transition-colors"
                  style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px' }}
                />
                <button
                  type="submit"
                  disabled={notifyLoading}
                  data-testid="edition2-notify-btn"
                  className="h-12 px-6 border transition-all duration-300 hover:bg-[rgba(200,168,64,0.08)] disabled:opacity-50"
                  style={{ borderColor: GOLD, fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.2em', color: GOLD }}
                >
                  {notifyLoading ? '...' : 'NOTIFY ME'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          PAGE FLIP READER MODAL (preserved)
          ═══════════════════════════════════════════ */}
      {isReaderOpen && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#FAF8F2' }}>
          <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(13,13,13,0.1)' }}>
            <button onClick={() => setIsReaderOpen(false)} className="flex items-center gap-2 transition-colors" style={{ color: 'rgba(13,13,13,0.6)' }} data-testid="close-reader-btn">
              <X size={20} />
              <span style={{ fontFamily: "'DM Mono', monospace" }} className="text-sm">Close</span>
            </button>
            <p className="text-xs tracking-wider" style={{ fontFamily: "'DM Mono', monospace", color: GOLD }}>PAGE {currentPage + 1} OF {totalPages}</p>
            <div className="w-20" />
          </div>

          <div className="flex-1 flex items-center justify-center relative px-4 py-4 overflow-hidden">
            <button onClick={prevPage} className="absolute left-2 md:left-6 z-10 p-2 md:p-3 rounded-full transition-all" style={{ backgroundColor: 'rgba(13,13,13,0.05)', color: 'rgba(13,13,13,0.6)' }} disabled={currentPage === 0} data-testid="prev-page-btn">
              <ChevronLeft size={24} />
            </button>

            <HTMLFlipBook ref={bookRef} width={380} height={540} size="stretch" minWidth={280} maxWidth={450} minHeight={400} maxHeight={650} showCover={true} mobileScrollSupport={true} onFlip={onFlip} className="book-shadow" drawShadow={true} flippingTime={600} usePortrait={false} maxShadowOpacity={0.3} showPageCorners={true}>
              <PageCover><img src={BOOK_COVERS.front} alt="Front Cover" className="w-full h-full object-cover" /></PageCover>

              <PageContent number="i">
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <p className="text-[10px] tracking-[0.3em] mb-10" style={{ fontFamily: "'DM Mono', monospace", color: GOLD }}>EDITION 1 &middot; 2026</p>
                  <h1 className="text-4xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#121212' }}>SHE</h1>
                  <h1 className="text-4xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#121212' }}>BUILT</h1>
                  <h1 className="text-4xl mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD }}>THIS</h1>
                  <div className="w-12 h-[1px] mb-6" style={{ backgroundColor: 'rgba(13,13,13,0.2)' }} />
                  <p className="text-base mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#121212' }}>Dr. Neha Dewan</p>
                  <p className="text-xs italic" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.5)' }}>Founder, She Built This</p>
                </div>
              </PageContent>

              <PageContent number="ii" dark={true}>
                <div className="flex-1 flex flex-col items-center justify-center text-center px-3">
                  <div className="w-10 h-[1px] mb-5" style={{ backgroundColor: GOLD }} />
                  <p className="text-[9px] tracking-[0.2em] mb-2" style={{ fontFamily: "'DM Mono', monospace", color: GOLD }}>Shr&#299; R&#257;dh&#257;k&#7771;&#7779;&#7751;a-cara&#7751;au vande</p>
                  <p className="text-[11px] italic mb-2" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(200,164,72,0.7)' }}>I bow at the sacred feet of Shri Radha and Krishna.</p>
                  <p className="text-[9px] mb-5" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(200,164,72,0.5)' }}>R&#257;dhe K&#7771;&#7779;&#7751;a R&#257;dhe K&#7771;&#7779;&#7751;a &middot; K&#7771;&#7779;&#7751;a K&#7771;&#7779;&#7751;a R&#257;dhe R&#257;dhe</p>
                  <div className="w-6 h-[1px] mb-5" style={{ backgroundColor: GOLD }} />
                  <p className="text-base mb-3" style={{ fontFamily: "'EB Garamond', serif", color: '#FFFFFF' }}>To my divine Radha Krishna.</p>
                  <div className="text-[12px] leading-[1.7]" style={{ fontFamily: "'EB Garamond', serif", color: '#F5F2EC' }}>
                    <p className="mb-2">You were the light before I knew I needed light.</p>
                    <p className="mb-2">Every word in this book was written in the hours you gave me.</p>
                    <p className="mb-2">Every woman documented on this platform was found through the eyes you gave me to see with.</p>
                  </div>
                  <p className="text-[13px] italic mt-4" style={{ fontFamily: "'EB Garamond', serif", color: GOLD }}>I did not build this.<br />You built it through me.</p>
                </div>
              </PageContent>

              <PageContent number="iii" dark={true}>
                <div className="flex-1 flex flex-col items-center justify-center text-center px-3">
                  <div className="w-6 h-[1px] mb-4" style={{ backgroundColor: GOLD }} />
                  <p className="text-base mb-3" style={{ fontFamily: "'EB Garamond', serif", color: '#FFFFFF' }}>To my parents.</p>
                  <div className="text-[12px] leading-[1.7] mb-4" style={{ fontFamily: "'EB Garamond', serif", color: '#F5F2EC' }}>
                    <p className="mb-1">You never told me what I could not do.</p>
                    <p className="mb-1">You never told me what I should not build.</p>
                    <p className="mb-2">You gave me the room.</p>
                    <p className="mb-1">You sat at the centre of every table and you made sure I knew that I belonged there too.</p>
                    <p className="font-semibold mt-2">Everything I know about building I learned from watching you love.</p>
                  </div>
                  <div className="w-6 h-[1px] mb-4" style={{ backgroundColor: GOLD }} />
                  <p className="text-base mb-3" style={{ fontFamily: "'EB Garamond', serif", color: '#FFFFFF' }}>To my family and my friends.</p>
                  <div className="text-[12px] leading-[1.7]" style={{ fontFamily: "'EB Garamond', serif", color: '#F5F2EC' }}>
                    <p className="mb-1">The ones who never questioned the evenings.</p>
                    <p className="mb-1">The ones who texted at midnight: how is the platform coming?</p>
                    <p className="mb-1">The ones who said: I knew you would.</p>
                    <p className="font-semibold">The ones who never once asked why.</p>
                  </div>
                </div>
              </PageContent>

              <PageContent number="iv" dark={true}>
                <div className="flex-1 flex flex-col items-center justify-center text-center px-3">
                  <div className="w-6 h-[1px] mb-5" style={{ backgroundColor: GOLD }} />
                  <div className="text-[13px] leading-[1.8]" style={{ fontFamily: "'EB Garamond', serif", color: '#F5F2EC' }}>
                    <p className="mb-2">And to every woman who spoke in the meeting.</p>
                    <p className="mb-2">The one who heard nothing back.</p>
                    <p className="text-lg font-bold mt-4" style={{ color: '#2E8B57' }}>And spoke again anyway.</p>
                  </div>
                  <div className="w-6 h-[1px] my-5" style={{ backgroundColor: GOLD }} />
                  <p className="text-[11px] tracking-[0.08em]" style={{ fontFamily: "'DM Mono', monospace", color: '#888880' }}>Your name belongs in this book.</p>
                </div>
              </PageContent>

              <PageContent number="1">
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <p className="text-[10px] tracking-[0.2em] mb-4" style={{ fontFamily: "'DM Mono', monospace", color: GOLD }}>PROLOGUE</p>
                  <div className="w-12 h-[1px] mb-4" style={{ backgroundColor: 'rgba(13,13,13,0.2)' }} />
                  <h2 className="text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#121212' }}>The Meeting</h2>
                  <p className="text-sm italic mt-6 px-4" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.5)' }}>"She had prepared for three weeks."</p>
                </div>
              </PageContent>

              <PageContent number="2">
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin">
                  <div className="text-[11px] leading-[1.75]" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.85)' }}>
                    <p className="mb-3 first-letter:text-xl first-letter:font-bold first-letter:text-[#C8A448] first-letter:float-left first-letter:mr-1">She had prepared for three weeks. Not because the meeting required three weeks of preparation. It did not. The analysis she brought to that room could have been assembled in an afternoon by someone who had been given the right to assemble it without apology.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>But she had learned, over the years, that preparation was her armour. Not confidence. Not seniority. Not the accumulated weight of a track record that was, by any objective measure, stronger than almost anyone else in that room.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>Preparation. The kind that is so thorough it becomes unassailable. The kind that says, before a single word is spoken: there is nothing you can find wrong with this work.</p>
                    <p className="text-center italic my-4 text-[12px]" style={{ color: GOLD }}>So she prepared for three weeks.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>She arrived early. She sat in the conference room alone for nine minutes before anyone else came in, going over her notes one more time even though she knew them.</p>
                  </div>
                </div>
              </PageContent>

              <PageContent number="3">
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin">
                  <div className="text-[11px] leading-[1.75]" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.85)' }}>
                    <p className="mb-3">When the others arrived, she watched them arrange themselves. The way rooms arrange themselves is a kind of grammar that can be read if you know the alphabet.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>The chairs closest to the head of the table and who takes them without hesitation. The laptops opened immediately by people who feel entitled to work. The coffee cups filled without asking, and the coffee cups never filled at all by the people who have learned that certain rooms do not refill your cup.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>She had been reading this grammar for fifteen years. She had become very good at reading it.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>She sat in the middle of the table. Not at the head. Not at the edges. The middle. The place that says: I am of this room. I belong here.</p>
                    <p className="mb-3 font-semibold" style={{ color: '#121212' }}>She spoke first.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>She laid out the analysis clearly. Structured. Evidence-based. The kind of presentation that, had it come from a different mouth, would have been described as authoritative.</p>
                  </div>
                </div>
              </PageContent>

              <PageContent number="4">
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin">
                  <div className="text-[11px] leading-[1.75]" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.85)' }}>
                    <p className="mb-3">When she finished, the room was quiet. Not the quiet of consideration. The particular quiet of a room that has decided, collectively and without discussion, to respond to what was just said by saying nothing.</p>
                    <p className="text-center italic my-4 text-[13px] px-2 py-2 border-l-2" style={{ color: GOLD, borderColor: '#2E8B57', backgroundColor: 'rgba(200,164,72,0.05)' }}>Four minutes passed. She counted them.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>Four minutes later, a man on the opposite side of the table said almost exactly what she had said. He said it less precisely. He said it with less data. But he said it with a particular quality of voice that certain rooms are designed to hear.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>The room responded to him. Not loudly. Not rudely. Worse than that: naturally. As if this was the first time the idea had been introduced.</p>
                    <p className="mb-3 font-bold" style={{ color: '#121212' }}>Her name was not in the minutes.</p>
                  </div>
                </div>
              </PageContent>

              <PageContent number="5">
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin">
                  <div className="text-[11px] leading-[1.75]" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.85)' }}>
                    <p className="mb-3">I know what happened after the meeting ended. She gathered her papers. She walked to the lift. She pressed the button.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>And in the eleven seconds it took for the lift to arrive, standing alone in the corridor, she allowed herself to feel it.</p>
                    <p className="text-center italic my-4 text-[12px]" style={{ color: '#888880' }}>Eleven seconds. Then the lift doors opened and she was professional again.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>I know this because I have been her. Not in that specific corridor, not on that specific afternoon, but in enough corridors on enough afternoons that the eleven seconds are as familiar to me as my own handwriting.</p>
                    <p className="mb-3 font-semibold" style={{ color: '#121212' }}>This book is what I did with it.</p>
                    <p className="text-center italic mt-4 text-[12px]" style={{ color: GOLD }}>Not in spite of the room. Not by waiting for it to change. By building a different one.</p>
                  </div>
                </div>
              </PageContent>

              <PageContent number="6">
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <p className="text-[10px] tracking-[0.2em] mb-4" style={{ fontFamily: "'DM Mono', monospace", color: GOLD }}>CHAPTER ONE</p>
                  <div className="w-12 h-[1px] mb-4" style={{ backgroundColor: 'rgba(13,13,13,0.2)' }} />
                  <h2 className="text-2xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#121212' }}>What Fifty Years Found</h2>
                  <p className="text-[11px] italic mt-6 px-4" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.5)' }}>"We have spent billions on the wrong answer."<br /><span className="text-[10px]">— The Research, 2026</span></p>
                </div>
              </PageContent>

              <PageContent number="7">
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin">
                  <div className="text-[11px] leading-[1.75]" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.85)' }}>
                    <p className="mb-3 first-letter:text-xl first-letter:font-bold first-letter:text-[#C8A448] first-letter:float-left first-letter:mr-1">In 1972, a researcher sat down with a stack of studies and a question she could not stop asking: What works?</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>What actually, measurably, reproducibly produces more women in positions of leadership? Not which programs feel meaningful. Not which initiatives receive the most funding. Which ones actually move the needle on the only number that matters: the number of women who reach the top?</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>She expected to find answers. The field was young then. Enthusiastic. Convinced that the problem was a knowledge problem. Give them the knowledge, ran the theory, and the system would correct itself.</p>
                    <p className="mb-3 font-semibold" style={{ color: '#121212' }}>She spent her career measuring the outcomes.</p>
                  </div>
                </div>
              </PageContent>

              <PageContent number="8">
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin">
                  <div className="text-[11px] leading-[1.75]" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.85)' }}>
                    <p className="mb-3">There was a program at a major corporation in the mid-1980s. A women's leadership accelerator with a seven-figure annual budget. It produced what looked, in the short term, like results.</p>
                    <p className="text-center italic my-3 text-[12px]" style={{ color: GOLD }}>Two years. Then the data went flat.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>By year five, the women who had been through the program were advancing at exactly the same rate as women who had not. The corporation had spent twelve years and tens of millions of dollars generating a Hawthorne effect and calling it leadership development.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>When the researcher presented this finding, the response was not to shut down the program. The response was to redesign the measurement.</p>
                    <p className="mb-3 font-semibold" style={{ color: '#121212' }}>That response is, in miniature, the story of fifty years of women's leadership development.</p>
                  </div>
                </div>
              </PageContent>

              <PageContent number="9">
                <div className="flex-1 flex flex-col items-center justify-center text-center px-3">
                  <p className="text-[11px] mb-4" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.7)' }}>The global investment in women's leadership development now exceeds eight billion dollars annually.</p>
                  <p className="text-[11px] mb-6" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.7)' }}>The question, after fifty years, remains: what works?</p>
                  <p className="text-[10px] tracking-[0.15em] mb-2" style={{ fontFamily: "'DM Mono', monospace", color: '#888880' }}>THE ANSWER IS</p>
                  <p className="text-6xl font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: GOLD }}>0</p>
                  <p className="text-[10px] tracking-[0.15em] mb-6" style={{ fontFamily: "'DM Mono', monospace", color: '#888880' }}>ZERO</p>
                  <div className="w-8 h-[1px] mb-4" style={{ backgroundColor: 'rgba(13,13,13,0.2)' }} />
                  <p className="text-[11px] italic px-4" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.7)' }}>Zero programs have ever been proven to produce more women at the top.</p>
                </div>
              </PageContent>

              <PageContent number="10">
                <div className="flex-1 overflow-y-auto pr-1 scrollbar-thin">
                  <div className="text-[11px] leading-[1.75]" style={{ fontFamily: "'EB Garamond', serif", color: 'rgba(13,13,13,0.85)' }}>
                    <p className="mb-3">Zero does not mean women cannot lead. Five hundred and ninety-five verified profiles say otherwise.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>Zero does not mean organisations have not tried. They have tried extraordinarily hard, at extraordinary expense, with extraordinary sincerity, for five decades.</p>
                    <p className="text-center italic my-4 text-[12px] px-2 py-2 border-l-2" style={{ color: GOLD, borderColor: '#2E8B57', backgroundColor: 'rgba(200,164,72,0.05)' }}>Zero means we have been solving the wrong problem for fifty years.</p>
                    <p className="mb-3" style={{ textIndent: '1.2em' }}>The programs are not solving the problem because the programs are aimed at women. The problem is not women. The problem is the system that was built before women arrived, and has not yet been persuaded to rebuild itself for them.</p>
                    <p className="mb-3 font-semibold" style={{ color: '#121212' }}>The system does not change when the women in it change. The system changes when the structure changes.</p>
                    <p className="text-center italic mt-3 text-[12px]" style={{ color: '#2E8B57' }}>These women changed the structure.</p>
                  </div>
                </div>
              </PageContent>

              <PageContent number="11" dark={true}>
                <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
                  <div className="w-full h-[1px] mb-6" style={{ backgroundColor: 'rgba(200,164,72,0.4)' }} />
                  <p className="text-base italic mb-4" style={{ fontFamily: "'EB Garamond', serif", color: '#888880' }}>This is where the free reading ends.</p>
                  <p className="text-sm mb-6" style={{ fontFamily: "'EB Garamond', serif", color: '#F5F2EC' }}>The full book — Edition 1 — 21 chapters, 595 women — is available now.</p>
                  <a href="https://a.co/d/0jdiF8K1" target="_blank" rel="noopener noreferrer" className="h-10 px-6 text-xs tracking-[0.05em] uppercase flex items-center justify-center mb-4" style={{ fontFamily: "'DM Mono', monospace", backgroundColor: GOLD, color: '#08080C' }}>GET THE BOOK</a>
                  <Link to="/nominate" onClick={() => setIsReaderOpen(false)} className="text-[11px] tracking-[0.05em] uppercase mb-4" style={{ fontFamily: "'DM Mono', monospace", color: GOLD }}>NOMINATE A WOMAN &rarr;</Link>
                  <div className="w-16 h-[1px] mb-4 mx-auto" style={{ backgroundColor: 'rgba(200,164,72,0.3)' }} />
                  <p className="text-[9px] tracking-[0.05em] mb-1" style={{ fontFamily: "'DM Mono', monospace", color: GOLD }}>&copy; 2025 SHE BUILT THIS</p>
                  <p className="text-[8px]" style={{ fontFamily: "'DM Mono', monospace", color: '#888880' }}>UAE Copyright Registered &middot; Amazon KDP</p>
                </div>
              </PageContent>

              <PageCover><img src={BOOK_COVERS.back} alt="Back Cover" className="w-full h-full object-cover" /></PageCover>
            </HTMLFlipBook>

            <button onClick={nextPage} className="absolute right-2 md:right-6 z-10 p-2 md:p-3 rounded-full transition-all" style={{ backgroundColor: 'rgba(13,13,13,0.05)', color: 'rgba(13,13,13,0.6)' }} data-testid="next-page-btn">
              <ChevronRight size={24} />
            </button>
          </div>

          <div className="text-center py-3 border-t" style={{ borderColor: 'rgba(13,13,13,0.1)' }}>
            <p className="text-xs" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(13,13,13,0.4)' }}>Click page edges or use arrows to turn pages</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bookFloat {
          0% { transform: translateY(0px); }
          100% { transform: translateY(-8px); }
        }
        .scrollbar-thin { scrollbar-width: thin; }
        .scrollbar-thin::-webkit-scrollbar { width: 3px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background-color: rgba(200,164,72,0.3); border-radius: 2px; }
      `}</style>
    </div>
  );
};

export default BookLaunchPage;
